import { Dimmer, Grid, Loader, Modal } from 'semantic-ui-react';
import { CountryDto, CountrySearchRequest, UpsertCountryDto } from "ts-types/api.types";
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { debounce } from "ts-debounce";
import { FormState } from "final-form";
import VirtualizedTable from 'components/layout/VirtualizedTable';
import UpsertCountryForm from 'components/settings/countres/UpsertCountryForm';
import { deleteCountry, findCountryById, searchCountries } from 'services/countryServices';
import { noop } from 'utils/functionUtils';
import DeleteActionButton from 'components/DeleteActionButton';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import CheckBox from 'components/final-form/CheckBox';
import SearchResultComponent from 'components/SearchResultComponent';

const initialCountrySearch: Partial<CountrySearchRequest> = {
  includesInactive: false
};

interface  CountryFieldProps extends FormState {
  values: Partial<CountrySearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundCountries: Array<CountryDto>,
  countriesLoaded: boolean,
  country?: UpsertCountryDto,
  countryId?: number,
  addEditModalOpen: boolean,
  loading: boolean,
  searchValues: Partial<CountrySearchRequest>,
  cancelTokenSource: CancelTokenSource
  isDefault: boolean,
}

class CountryView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundCountries: [],
      countriesLoaded: false,
      country: undefined,
      countryId: undefined,
      addEditModalOpen: false,
      loading: false,
      searchValues: initialCountrySearch,
      isDefault: false,
      cancelTokenSource
    }

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id: number | undefined) => {
    const {cancelTokenSource} = this.state;

    if (id) {
      findCountryById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, country?: UpsertCountryDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        country: country,
        countryId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      countryId: undefined,
      country: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        countriesLoaded: true
      })
    };

    searchCountries(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundCountries: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deleteCountry = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteCountry(id, cancelTokenSource).then(response => {
      this.setState(({foundCountries}: State) => ({
        foundCountries: foundCountries.filter((country: CountryDto) => country.id != response.id)
      }))
    }).catch(noop);
  };

  handleChange = debounce(({values}: CountryFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;
    this.setState({countriesLoaded: false});

    const onFinally = () => {
      this.setState({
        countriesLoaded: true
      })
    };

    searchCountries(values, cancelTokenSource)
        .then(response =>
            this.setState({
              foundCountries: response,
              searchValues: values
            })
        )
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {countriesLoaded} = this.state;
    return (
        <PraxStyledSegment>

          <div>
            {this.renderSearchCountriesSegment()}
          </div>

          <StyledViewTableRow>
            {!countriesLoaded ? this.renderLoader() : this.renderCountriesSegment()}
          </StyledViewTableRow>

          {this.renderUpsertCountryModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchCountriesSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{
              includesInactive: false
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundCountries} = this.state;

    const numResults = foundCountries.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="countrySearchTerm"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={4} verticalAlign={"top"}>
              <Field
                  name="includesInactive"
                  component={CheckBox}
                  label={getMessage("includingInactive")}
                  toggle
              />
            </Grid.Column>
            <Grid.Column width={9}>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    );
  };

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    if (cellData) {
      return getMessage("country.active")
    }
    return getMessage("country.inactive");
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("country.confirmDelete")}
              onConfirm={() => this.deleteCountry(cellData)}
          />
        </React.Fragment>
    );
  };

  countryRowGetter = ({index}: any) => {
    const {foundCountries} = this.state;

    Object.assign(foundCountries[index], {index: index + 1});
    return foundCountries[index]
  };

  foundCountriesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundCountries} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundCountries[index].id)}
        >
          {columns}
        </div>
    );
  };

  renderCountriesSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundCountries} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundCountries.length}
            rowGetter={this.countryRowGetter}
            rowRenderer={this.foundCountriesRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 200,
                label: (getMessage("country.shortName")),
                dataKey: "shortName"
              },
              {
                width: 500,
                flexGrow: 1,
                label: (getMessage("country.description")),
                dataKey: "name"
              },
              {
                width: 200,
                label: (getMessage("country.isoCode")),
                dataKey: "isoCode"
              },
              {
                width: 150,
                flexGrow: 1,
                label: (getMessage("status")),
                dataKey: "active",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("country.loading")}</Loader>
        </Dimmer>
    );
  };

  renderUpsertCountryModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, country, countryId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("countries")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertCountryForm
                country={country}
                countryId={countryId}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }
}

export default withMessages(CountryView);
